/*
 * Copyright (c) 2022, Salesforce, Inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */

import React, {useEffect} from 'react'
import {getConfig} from 'pwa-kit-runtime/utils/ssr-config'
import PropTypes from 'prop-types'
import {useLocation} from 'react-router-dom'

// Components
import {Box, Container} from '@chakra-ui/react'
import {FormattedMessage} from 'react-intl'

// Project Components
import Seo from '../../components/seo'
import CMSContent from '../../components/cms-content'
import InteractionStudioContent from '../../components/interaction-studio/content'
import InteractionStudioRecommendedProducts from '../../components/interaction-studio/recommended-products'
import RecommendedProducts from '../../components/recommended-products'

// Others
import {useInitialDataLayer} from '../../tracking/hooks/use-initial-data-layer'
import {preloadCmsContent} from '../../components/cms-content/preload-cms-content'

// Hooks
import useEinstein from '../../commerce-api/hooks/useEinstein'
import useMultiSite from '../../hooks/use-multi-site'

// Constants
import {HOME_CONTENT_ASSET_ID} from '../../constants'
import useInteractionStudioPixel from '../../tracking/interaction-studio'
import useEffectEvent from '../../hooks/use-effect-event'
import HeaderContent from '../../components/header-content'

/**
 * This is the home page for Retail React App.
 * The page is created for demonstration purposes.
 * The page renders SEO metadata and a few promotion
 * categories and products, data is from local file.
 */
const Home = ({preloadedContent, rootFolder}) => {
    const einstein = useEinstein()
    const {pathname} = useLocation()
    const {site} = useMultiSite()

    useInitialDataLayer({pageType: 'Homepage'})
    useInteractionStudioPixel()

    /**************** Einstein ****************/
    const onMount = useEffectEvent(() => einstein.sendViewPage(pathname))
    useEffect(() => {
        onMount()
    }, [onMount])

    return (
        <>
            <HeaderContent />
            <Box data-testid="home-page" sx={{px: {base: 0, lg: 6, xl: 8}}}>
                <Seo
                    title={rootFolder?.pageTitle}
                    description={rootFolder?.pageDescription}
                    keywords={rootFolder?.pageKeywords}
                />
                <InteractionStudioContent id="is-home-content">
                    <CMSContent
                        content={preloadedContent}
                        id={HOME_CONTENT_ASSET_ID}
                        showError={false}
                    />
                </InteractionStudioContent>
                {/* Product Recommendations */}
                <InteractionStudioRecommendedProducts id="is-home-page-recommendations">
                    <RecommendedProducts
                        wrapper={({children}) => (
                            <Container
                                paddingY={12}
                                paddingX={{base: 4, md: 6, lg: 12}}
                                background="stokkeGray.bright"
                            >
                                {children}
                            </Container>
                        )}
                        title={
                            <FormattedMessage
                                defaultMessage="Other families also like"
                                id="recommended_products.title.other_families_also_like"
                            />
                        }
                        recommender={`HP_recom_${site.id}`}
                    />
                </InteractionStudioRecommendedProducts>
            </Box>
        </>
    )
}

Home.getTemplateName = () => 'home'

Home.shouldGetProps = ({previousLocation, location}) =>
    !previousLocation || previousLocation.pathname !== location.pathname

Home.getProps = async ({res, api}) => {
    if (res) {
        const cacheConfig = getConfig().app.cache
        if (cacheConfig?.home) {
            res.set('Cache-Control', cacheConfig.home)
        }
    }

    const [preloadedContent, rootFolder] = await Promise.all([
        preloadCmsContent(HOME_CONTENT_ASSET_ID, {api}),
        api.shopperFolders.getFolder({parameters: {fdid: 'root'}})
    ])
    return {preloadedContent, rootFolder}
}

Home.propTypes = {
    /**
     * The current state of `getProps` when running this value is `true`, otherwise it's
     * `false`. (Provided internally)
     */
    isLoading: PropTypes.bool,
    preloadedContent: PropTypes.object,
    rootFolder: PropTypes.object
}

export default Home
